import { useState } from 'react'
import { useSessionStore } from '../stores/session.store.js'

/**
 * Banner discreto en el menú que permite re-mostrar y compartir el código de
 * la sesión de mesa OhSir. Lo ve cualquier comensal de la sesión: el primero
 * para invitar a sus acompañantes, los que se unieron por si llega alguien más.
 *
 * Plegado por defecto para no competir con el menú. Si el navegador soporta
 * `navigator.share`, ofrece el share-sheet nativo; si no, copia al portapapeles.
 */
export function ShareCodeCard() {
  const session = useSessionStore((s) => s.session)
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)

  const code = session?.joinCode
  if (!code) return null

  // Modo-consciente: en TAKEOUT no hay mesa que compartir — el código es la
  // referencia del PEDIDO para llevar (seguimiento/recoger), no un código de mesa.
  const isTakeout = session?.orderMode === 'TAKEOUT'
  const codeLabel = isTakeout ? 'Código de pedido' : 'Código de la mesa'
  const bodyText = isTakeout
    ? 'Guarda este código para dar seguimiento a tu pedido y recogerlo en el mostrador.'
    : 'Compártelo con quienes lleguen a tu mesa para que se unan a esta misma cuenta.'
  const actionLabel = isTakeout ? (copied ? '✓ Código copiado' : 'Copiar código') : (copied ? '✓ Código copiado' : 'Compartir código')

  async function share(): Promise<void> {
    const text = isTakeout
      ? `Mi pedido para llevar en NintaiGO — código ${code}`
      : `Únete a mi mesa en NintaiGO con el código ${code}`
    try {
      // En mesa ofrecemos el share-sheet nativo (invitar acompañantes); en takeout
      // basta copiar el código (no se comparte con nadie más).
      if (!isTakeout && typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share({ title: 'Código de mesa NintaiGO', text })
        return
      }
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(code as string)
        setCopied(true)
        setTimeout(() => setCopied(false), 2500)
      }
    } catch {
      /* el usuario canceló el share-sheet — sin ruido */
    }
  }

  return (
    <div className="card share-code-card">
      <button
        type="button"
        className="share-code-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span>
          <strong>{codeLabel}:</strong>{' '}
          <span className="share-code-inline">{code}</span>
        </span>
        <span className="share-code-chevron">
          {open ? 'Cerrar' : (isTakeout ? 'Ver' : 'Compartir')}
          <span className="chev" aria-hidden="true">▾</span>
        </span>
      </button>
      {open && (
        <div className="share-code-body">
          <p className="muted">{bodyText}</p>
          <button type="button" className="btn btn-primary" onClick={share}>
            {actionLabel}
          </button>
        </div>
      )}
    </div>
  )
}
